<template>
  <div class="cs-root">
    <transition name="cs-fade">
      <div v-show="visible" class="cs-mask" @click="close"></div>
    </transition>

    <transition name="cs-slide">
      <div v-show="visible" class="cs-sheet" :style="sheetStyle">
        <!-- 顶栏：居中手柄，按住往下拖或点一下关闭 -->
        <div
          class="cs-header"
          @touchstart="onDragStart"
          @touchmove.prevent="onDragMove"
          @touchend="onDragEnd"
          @touchcancel="onDragEnd"
        >
          <div class="cs-handle-area" @click="close"><span class="cs-handle"></span></div>
          <div class="cs-title">
            <CanvasText text="在线客服" :font-size="0.45" unit="rem" color="#fff" font-weight="600" />
            <CanvasText
              v-if="statusText"
              class="cs-status"
              :text="statusText"
              :font-size="0.3"
              unit="rem"
              color="#8e8e93"
            />
          </div>
        </div>

        <!-- 消息列表 -->
        <div ref="list" class="cs-list" @scroll="onScroll">
          <div v-if="loadingOlder" class="cs-hint">
            <CanvasText text="加载中…" :font-size="0.32" unit="rem" color="#8e8e93" />
          </div>

          <div v-if="phase === 'loading'" class="cs-center">
            <CanvasText text="正在连接客服…" :font-size="0.37" unit="rem" color="#8e8e93" />
          </div>
          <div v-else-if="phase === 'error'" class="cs-center" @click="start">
            <CanvasText text="连接失败，点击重试" :font-size="0.37" unit="rem" color="#E8BD70" />
          </div>
          <template v-else>
            <div v-if="!hasOlder && !messages.length" class="cs-welcome">
              <CanvasText
                text="您好，请问有什么可以帮您？"
                :font-size="0.37"
                unit="rem"
                color="#c7c7cc"
                align="center"
              />
            </div>

            <div v-for="(m, i) in messages" :key="m.key">
              <div v-if="showTime(i)" class="cs-time">{{ formatTime(m.createdAt) }}</div>

              <div v-if="m.type === 2" class="cs-activity">{{ m.content }}</div>

              <div v-else class="cs-row" :class="{ me: m.type === 0 }">
                <img
                  v-if="m.type !== 0"
                  class="cs-avatar"
                  :src="(m.sender && m.sender.thumbnail) || csIcon"
                  @error="$event.target.src = csIcon"
                />
                <div class="cs-bubble-wrap">
                  <div
                    v-for="(a, ai) in m.attachments"
                    :key="'a' + ai"
                    class="cs-media"
                    :class="{ pending: m.status !== 'sent' }"
                  >
                    <img
                      v-if="a.fileType === 'image'"
                      :src="a.thumbUrl || a.dataUrl"
                      :style="mediaStyle(a)"
                      @click="preview = a.dataUrl"
                      @load="onMediaLoad"
                    />
                    <video
                      v-else-if="a.fileType === 'video'"
                      :src="a.dataUrl"
                      :style="mediaStyle(a)"
                      controls
                      playsinline
                      preload="metadata"
                    ></video>
                    <a v-else class="cs-file" :href="a.dataUrl" target="_blank" rel="noopener noreferrer">
                      {{ a.name || '查看附件' }}
                    </a>
                    <div v-if="m.status === 'sending' && m.progress != null" class="cs-progress">
                      {{ m.progress }}%
                    </div>
                  </div>

                  <div v-if="m.content" class="cs-bubble">
                    <template v-for="(part, pi) in linkify(m.content)">
                      <a
                        v-if="part.url"
                        :key="pi"
                        :href="part.url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >{{ part.text }}</a>
                      <span v-else :key="pi">{{ part.text }}</span>
                    </template>
                  </div>
                </div>
                <div v-if="m.type === 0 && m.status !== 'sent'" class="cs-state">
                  <span v-if="m.status === 'sending'" class="cs-spinner"></span>
                  <span v-else class="cs-failed" @click="retry(m)">!</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 输入栏 -->
        <div v-if="muted" class="cs-muted">
          <CanvasText text="您已被客服禁言，暂时无法发送消息" :font-size="0.37" unit="rem" color="#8e8e93" />
        </div>
        <div v-else class="cs-input">
          <textarea
            ref="input"
            v-model="draft"
            class="cs-textarea"
            rows="1"
            maxlength="2000"
            :disabled="phase !== 'ready'"
            @input="autoGrow"
            @keydown.enter.exact="onEnter"
          ></textarea>
          <div class="cs-send" :class="{ active: canSend }" @click="sendDraft">
            <CanvasText text="发送" :font-size="0.37" unit="rem" :color="canSend ? '#000' : '#8e8e93'" />
          </div>
          <label class="cs-pick" :class="{ disabled: phase !== 'ready' }">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#c7c7cc" stroke-width="1.8">
              <rect x="3" y="4" width="18" height="16" rx="3" />
              <circle cx="9" cy="10" r="1.8" />
              <path d="M21 16l-5-5-8 8" stroke-linejoin="round" />
            </svg>
            <input
              type="file"
              accept="image/*,video/mp4,video/quicktime,video/webm"
              :disabled="phase !== 'ready'"
              @change="onPickFile"
            />
          </label>
        </div>
      </div>
    </transition>

    <!-- 看大图 -->
    <div v-if="preview" class="cs-preview" @click="preview = null">
      <img :src="preview" />
    </div>
  </div>
</template>

<script>
import CanvasText from '@/components/CanvasText.vue'
import csIcon from '@/assets/icon-cs.png'
import CsCable from '@/cs/cable'
import { CS_WS_URL, csEnabled } from '@/cs/config'
import { getVisitor, clearVisitor, hasCachedVisitor } from '@/cs/session'
import {
  listConversations,
  createConversation,
  listMessages,
  sendText,
  sendFile,
  updateLastSeen,
  isMutedError,
  isRateLimited,
  isRetryable
} from '@/cs/api'

const PAGE_SIZE = 20
const MAX_FILE_SIZE = 40 * 1024 * 1024
const ALLOWED_VIDEO = ['video/mp4', 'video/quicktime', 'video/webm']
// 链接后面紧跟的中文标点不算链接
const URL_PATTERN = /(https?:\/\/[^\s<>"'，。！？；：、（）【】「」《》]+)/g

let echoSeq = 0
const newEchoId = () => `web-${Date.now().toString(36)}-${(echoSeq++).toString(36)}-${Math.random().toString(36).slice(2, 8)}`

// 服务端消息 → 页面用的格式
function normalize(raw) {
  const attrs = raw.content_attributes || {}
  return {
    key: raw.echo_id || attrs.client_echo_id || `id-${raw.id}`,
    id: raw.id,
    echoId: raw.echo_id || attrs.client_echo_id || null,
    content: raw.content || '',
    type: raw.message_type,
    createdAt: raw.created_at,
    attachments: (raw.attachments || []).map(a => ({
      fileType: a.file_type,
      dataUrl: a.data_url,
      thumbUrl: a.thumb_url,
      width: a.width,
      height: a.height,
      name: a.fallback_title
    })),
    sender: raw.sender || null,
    status: 'sent'
  }
}

// 大图先压缩再传（GIF 保留动图不压）：长边不超过 1920，JPEG 85%
function compressImage(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 1.5 * 1024 * 1024) return Promise.resolve(file)
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, 1920 / Math.max(img.width, img.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(img.width * scale)
      canvas.height = Math.round(img.height * scale)
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      canvas.toBlob(
        blob => resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file),
        'image/jpeg',
        0.85
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(file)
    }
    img.src = url
  })
}

export default {
  name: 'CsPanel',
  components: { CanvasText },
  data() {
    return {
      csIcon,
      visible: false,
      phase: 'idle', // idle / loading / ready / error
      cableState: 'disconnected',
      visitor: null,
      convId: null,
      messages: [],
      hasOlder: false,
      loadingOlder: false,
      muted: false,
      draft: '',
      preview: null,
      unread: 0,
      contactLastSeenAt: 0,
      viewport: null,
      dragY: 0,
      dragging: false
    }
  },
  computed: {
    canSend() {
      return this.phase === 'ready' && this.draft.trim().length > 0
    },
    statusText() {
      if (this.phase !== 'ready') return ''
      return this.cableState === 'connected' ? '' : '连接中…'
    },
    sheetStyle() {
      const style = {}
      // 手机键盘弹出时按可见区域收起聊天窗，输入框不被挡住
      if (this.viewport) {
        style.height = `${Math.round(this.viewport.height * 0.92)}px`
        style.bottom = `${this.viewport.bottom}px`
      }
      // 往下拖时跟手；松手后去掉，交还给弹出 / 收起动画
      if (this.dragging || this.dragY) {
        style.transform = `translate(-50%, ${this.dragY}px)`
        style.transition = this.dragging ? 'none' : 'transform 0.2s ease'
      }
      return style
    }
  },
  watch: {
    unread(count) {
      this.$emit('unread', count)
    }
  },
  mounted() {
    if (window.visualViewport) {
      this.onViewport = () => {
        const vv = window.visualViewport
        const bottom = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        this.viewport = bottom > 0 ? { height: vv.height, bottom } : null
      }
      window.visualViewport.addEventListener('resize', this.onViewport)
    }
    this.onPageVisible = () => document.visibilityState === 'visible' && this.markSeen()
    document.addEventListener('visibilitychange', this.onPageVisible)
    // 以前聊过的访客：后台连上，客服回复时在入口上显示红点。没聊过的不建访客，只有点开才建
    if (csEnabled() && hasCachedVisitor()) this.start({ silent: true })
  },
  beforeDestroy() {
    if (this.cable) this.cable.dispose()
    if (window.visualViewport && this.onViewport) window.visualViewport.removeEventListener('resize', this.onViewport)
    document.removeEventListener('visibilitychange', this.onPageVisible)
    document.body.style.overflow = ''
  },
  methods: {
    open() {
      if (!csEnabled()) {
        this.$toast('在线客服暂不可用')
        return
      }
      this.visible = true
      document.body.style.overflow = 'hidden'
      if (this.phase === 'idle' || this.phase === 'error') this.start()
      else this.markSeen()
      this.$nextTick(() => this.scrollToBottom())
    },
    close() {
      this.visible = false
      document.body.style.overflow = ''
      if (this.$refs.input) this.$refs.input.blur()
    },

    // ---------------------------------------------------------------- 建立连接
    async start({ silent = false } = {}) {
      if (this.phase === 'loading') return
      this.phase = 'loading'
      try {
        this.visitor = await getVisitor()
        const conversations = await listConversations(this.visitor.sourceId)
        const conv = conversations.length ? conversations[conversations.length - 1] : null
        this.convId = conv ? conv.id : null
        this.muted = Boolean(conv && conv.contact && conv.contact.blocked)
        this.contactLastSeenAt = (conv && conv.contact_last_seen_at) || 0
        await this.loadLatest()
        this.connectCable()
        this.phase = 'ready'
        // 后台连接期间访客点开了聊天窗：按已打开处理
        if (this.visible) this.markSeen()
        else if (silent) this.unread = this.countUnread()
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.phase = silent ? 'idle' : 'error'
      }
    },
    connectCable() {
      if (this.cable) this.cable.dispose()
      this.cable = new CsCable({
        url: CS_WS_URL,
        pubsubToken: this.visitor.pubsubToken,
        onEvent: this.onEvent,
        onState: state => (this.cableState = state),
        onReconnected: this.syncLatest,
        onRejected: this.restart
      })
      this.cable.connect()
    },
    // 访客身份在客服系统里被删了：重新建一个
    restart() {
      if (this.cable) this.cable.dispose()
      this.cable = null
      clearVisitor()
      this.messages = []
      this.convId = null
      this.phase = 'idle'
      if (this.visible) this.start()
    },

    // ---------------------------------------------------------------- 消息
    async loadLatest() {
      if (!this.convId) {
        this.hasOlder = false
        return
      }
      const list = await listMessages(this.visitor.sourceId, this.convId)
      list.map(normalize).forEach(m => this.upsert(m))
      this.hasOlder = list.length >= PAGE_SIZE
    },
    async loadOlder() {
      if (!this.hasOlder || this.loadingOlder || !this.convId) return
      const first = this.messages.find(m => m.id)
      if (!first) return
      this.loadingOlder = true
      const el = this.$refs.list
      const fromBottom = el.scrollHeight - el.scrollTop
      try {
        const list = await listMessages(this.visitor.sourceId, this.convId, first.id)
        list.map(normalize).forEach(m => this.upsert(m))
        this.hasOlder = list.length >= PAGE_SIZE
        this.$nextTick(() => (el.scrollTop = el.scrollHeight - fromBottom))
      } catch (e) {
        // 下次滚到顶再试
      } finally {
        this.loadingOlder = false
      }
    },
    // 断线重连后补齐断线期间的消息和禁言状态
    async syncLatest() {
      if (!this.visitor) return
      try {
        const conversations = await listConversations(this.visitor.sourceId)
        const conv = conversations.find(c => c.id === this.convId) || conversations[conversations.length - 1]
        if (conv) {
          this.convId = conv.id
          this.muted = Boolean(conv.contact && conv.contact.blocked)
        }
        const before = this.messages.length
        await this.loadLatest()
        if (this.messages.length !== before) this.afterIncoming()
      } catch (e) {
        // 下次重连再补
      }
    },
    // 按 echo_id / id 去重，按时间插入；返回是否新增
    upsert(m) {
      const i = this.messages.findIndex(x => (m.echoId && x.echoId === m.echoId) || (m.id && x.id === m.id))
      if (i >= 0) {
        const old = this.messages[i]
        // 本地预览图在服务端图片加载出来之前继续显示，避免闪一下
        if (old.localUrl && m.attachments.length) {
          m.attachments = m.attachments.map((a, ai) => ({ ...a, thumbUrl: a.thumbUrl || (old.attachments[ai] || {}).thumbUrl }))
        }
        this.$set(this.messages, i, { ...m, key: old.key })
        return false
      }
      const at = this.messages.findIndex(x => x.createdAt > m.createdAt)
      if (at < 0) this.messages.push(m)
      else this.messages.splice(at, 0, m)
      return true
    },
    onEvent(event, data) {
      switch (event) {
        case 'message.created':
        case 'message.updated': {
          if (data.conversation_id !== this.convId) return
          const m = normalize(data)
          const isNew = this.upsert(m)
          if (isNew && m.type !== 0) this.afterIncoming()
          return
        }
        case 'contact.blocked_updated':
          this.muted = data.blocked === true
          return
        case 'conversation.created':
          if (!this.convId && data.id) this.convId = data.id
          return
      }
    },
    afterIncoming() {
      if (this.visible && document.visibilityState === 'visible') {
        this.markSeen()
        this.$nextTick(() => this.scrollToBottom(true))
      } else {
        this.unread = this.countUnread()
      }
    },
    countUnread() {
      return this.messages.filter(m => m.type === 1 && m.createdAt > this.contactLastSeenAt).length
    },
    markSeen() {
      this.unread = 0
      if (!this.visible || !this.convId || !this.visitor) return
      this.contactLastSeenAt = Math.floor(Date.now() / 1000)
      updateLastSeen(this.visitor.sourceId, this.convId)
    },

    // ---------------------------------------------------------------- 发送
    async ensureConversation() {
      if (this.convId) return this.convId
      const conv = await createConversation(this.visitor.sourceId)
      this.convId = conv.id
      return conv.id
    },
    onEnter(e) {
      // 输入法选字时的回车不算；手机上回车照常换行，电脑上回车发送（Shift+回车换行）
      if (e.isComposing || e.keyCode === 229) return
      if (window.matchMedia('(pointer: coarse)').matches) return
      e.preventDefault()
      this.sendDraft()
    },
    sendDraft() {
      if (!this.canSend) return
      const content = this.draft.trim()
      this.draft = ''
      this.$nextTick(this.autoGrow)
      const m = this.localMessage({ content })
      this.deliver(m)
    },
    async onPickFile(e) {
      const picked = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!picked) return
      const isImage = /^image\//.test(picked.type)
      if (!isImage && ALLOWED_VIDEO.indexOf(picked.type) < 0) {
        this.$toast('只能发送图片或视频')
        return
      }
      const file = isImage ? await compressImage(picked) : picked
      if (file.size > MAX_FILE_SIZE) {
        this.$toast('文件不能超过 40MB')
        return
      }
      const localUrl = URL.createObjectURL(file)
      const m = this.localMessage({
        file,
        localUrl,
        attachments: [{ fileType: isImage ? 'image' : 'video', dataUrl: localUrl, thumbUrl: isImage ? localUrl : null }]
      })
      this.deliver(m)
    },
    localMessage(extra) {
      const echoId = newEchoId()
      const m = {
        key: echoId,
        id: null,
        echoId,
        content: '',
        type: 0,
        createdAt: Math.floor(Date.now() / 1000),
        attachments: [],
        sender: null,
        status: 'sending',
        progress: null,
        ...extra
      }
      this.messages.push(m)
      this.$nextTick(() => this.scrollToBottom(true))
      return m
    },
    async deliver(m) {
      this.patch(m, { status: 'sending', progress: m.file ? 0 : null })
      try {
        const convId = await this.ensureConversation()
        const sent = m.file
          ? await sendFile(this.visitor.sourceId, convId, m.file, m.echoId, p => this.patch(m, { progress: p }))
          : await sendText(this.visitor.sourceId, convId, m.content, m.echoId)
        this.upsert({ ...normalize(sent), echoId: m.echoId, localUrl: m.localUrl })
      } catch (e) {
        if (isMutedError(e)) this.muted = true
        const limited = isRateLimited(e)
        this.patch(m, { status: 'failed', retryable: limited || isRetryable(e) })
        if (limited) this.$toast('操作过于频繁，请稍后再试')
        else if (e && e.response && e.response.status === 422) this.$toast('文件格式不支持')
      }
    },
    retry(m) {
      if (this.muted) return
      if (m.retryable === false) {
        this.messages = this.messages.filter(x => x !== m && x.key !== m.key)
        this.$toast('该消息无法发送，已移除')
        return
      }
      this.deliver(m)
    },
    patch(m, values) {
      const i = this.messages.findIndex(x => x.key === m.key)
      if (i >= 0) this.$set(this.messages, i, { ...this.messages[i], ...values })
      Object.assign(m, values)
    },

    // ---------------------------------------------------------------- 界面
    onDragStart(e) {
      this.dragStartY = e.touches[0].clientY
      this.dragging = true
    },
    onDragMove(e) {
      if (!this.dragging) return
      this.dragY = Math.max(0, e.touches[0].clientY - this.dragStartY)
    },
    onDragEnd() {
      if (!this.dragging) return
      this.dragging = false
      // 拖过 90px 就关闭，否则弹回去
      if (this.dragY > 90) {
        this.dragY = 0
        this.close()
      } else {
        this.dragY = 0
      }
    },
    onScroll() {
      if (this.$refs.list.scrollTop < 40) this.loadOlder()
    },
    scrollToBottom(smooth) {
      const el = this.$refs.list
      if (!el) return
      if (smooth && el.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
      else el.scrollTop = el.scrollHeight
    },
    onMediaLoad() {
      // 最新几条里的图片加载完会撑高列表，贴底时跟着到底
      const el = this.$refs.list
      if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 400) this.scrollToBottom()
    },
    autoGrow() {
      const el = this.$refs.input
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 110) + 'px'
    },
    mediaStyle(a) {
      if (!a.width || !a.height) return {}
      const maxW = 180
      const maxH = 240
      const scale = Math.min(maxW / a.width, maxH / a.height, 1)
      // 用 rem（设计稿 375 宽时 1rem = 37.5px），和样式里的尺寸一起随页面宽度缩放
      return { width: `${((a.width * scale) / 37.5).toFixed(3)}rem`, height: `${((a.height * scale) / 37.5).toFixed(3)}rem` }
    },
    showTime(i) {
      if (i === 0) return true
      return this.messages[i].createdAt - this.messages[i - 1].createdAt > 5 * 60
    },
    formatTime(ts) {
      const d = new Date(ts * 1000)
      const pad = n => String(n).padStart(2, '0')
      const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
      const now = new Date()
      if (d.toDateString() === now.toDateString()) return hm
      if (d.getFullYear() === now.getFullYear()) return `${d.getMonth() + 1}月${d.getDate()}日 ${hm}`
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${hm}`
    },
    linkify(text) {
      const parts = []
      let last = 0
      String(text).replace(URL_PATTERN, (url, _g, offset) => {
        if (offset > last) parts.push({ text: text.slice(last, offset) })
        parts.push({ text: url, url })
        last = offset + url.length
        return url
      })
      if (last < text.length) parts.push({ text: text.slice(last) })
      return parts
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.55);
}
.cs-sheet {
  position: fixed;
  z-index: 1001;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  // 和页面一样最宽 10rem（电脑上是居中的手机宽度，见 utils/remCap.js）
  max-width: 10rem;
  height: 88vh;
  height: 88dvh;
  display: flex;
  flex-direction: column;
  background: #1c1c1e;
  border-radius: 14px 14px 0 0;
  overflow: hidden;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.5);
}
.cs-header {
  flex: none;
  padding: 0 18px 12px;
  touch-action: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(248, 191, 84, 0.12) 0%, rgba(248, 191, 84, 0) 100%);
}
.cs-handle-area {
  display: flex;
  justify-content: center;
  padding: 8px 0 10px;
  cursor: pointer;
}
.cs-handle {
  width: 36px;
  height: 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.35);
}
.cs-title {
  display: flex;
  align-items: baseline;
}
.cs-status {
  margin-left: 8px;
}
.cs-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 10px 12px 16px;
}
.cs-center {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.cs-hint,
.cs-welcome {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}
.cs-welcome {
  padding-top: 24px;
}
.cs-time {
  text-align: center;
  color: #8e8e93;
  font-size: 12px;
  margin: 14px 0 8px;
}
.cs-activity {
  text-align: center;
  color: #8e8e93;
  font-size: 12px;
  margin: 8px 30px;
}
.cs-row {
  display: flex;
  align-items: flex-start;
  margin: 8px 0;
  &.me {
    flex-direction: row-reverse;
  }
}
.cs-avatar {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  background: #2c2c2e;
}
.cs-bubble-wrap {
  max-width: 72%;
  display: flex;
  flex-direction: column;
  .me & {
    align-items: flex-end;
  }
}
.cs-bubble {
  padding: 9px 12px;
  border-radius: 4px 14px 14px 14px;
  background: #2c2c2e;
  color: #fff;
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  a {
    color: #f8bf54;
    text-decoration: underline;
  }
  .me & {
    border-radius: 14px 4px 14px 14px;
    background: #e8bd70;
    color: #000;
    a {
      color: #5a3b00;
    }
  }
}
.cs-media {
  position: relative;
  margin-bottom: 4px;
  border-radius: 10px;
  overflow: hidden;
  background: #2c2c2e;
  img,
  video {
    display: block;
    max-width: 180px;
    max-height: 240px;
    min-width: 60px;
    min-height: 60px;
    object-fit: cover;
    cursor: zoom-in;
  }
  &.pending img,
  &.pending video {
    opacity: 0.6;
  }
}
.cs-file {
  display: block;
  padding: 10px 12px;
  color: #f8bf54;
  font-size: 14px;
}
.cs-progress {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.35);
}
.cs-state {
  align-self: center;
  margin: 0 6px;
}
.cs-spinner {
  display: block;
  width: 14px;
  height: 14px;
  border: 2px solid #555;
  border-top-color: #e8bd70;
  border-radius: 50%;
  animation: cs-spin 0.8s linear infinite;
}
.cs-failed {
  display: block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  border-radius: 50%;
  background: #ff3b30;
  color: #fff;
  text-align: center;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
}
@keyframes cs-spin {
  to {
    transform: rotate(360deg);
  }
}
.cs-input {
  flex: none;
  display: flex;
  align-items: flex-end;
  padding: 8px 10px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #1c1c1e;
}
.cs-pick {
  position: relative;
  flex: none;
  width: 38px;
  height: 38px;
  margin-left: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  svg {
    width: 26px;
    height: 26px;
  }
  input {
    position: absolute;
    top: 0;
  right: 0;
  bottom: 0;
  left: 0;
    opacity: 0;
    cursor: pointer;
  }
  &.disabled {
    opacity: 0.4;
  }
}
.cs-textarea {
  flex: 1;
  // 初始一行高，和两侧按钮对齐；输入多行时由 autoGrow 撑高（最多约 4 行）
  height: 38px;
  min-height: 38px;
  max-height: 110px;
  margin: 0 8px 0 0;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  outline: none;
  resize: none;
  background: #2c2c2e;
  color: #fff;
  // 16px 以下 iOS 聚焦时会自动放大页面
  font-size: 16px;
  line-height: 22px;
  font-family: inherit;
}
.cs-send {
  flex: none;
  height: 38px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  border-radius: 8px;
  background: #3a3a3c;
  cursor: pointer;
  &.active {
    background: #e8bd70;
  }
}
.cs-muted {
  flex: none;
  display: flex;
  justify-content: center;
  padding: 14px 10px;
  padding-bottom: calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.cs-preview {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.92);
  img {
    max-width: 100%;
    max-height: 100%;
  }
}
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.25s;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
}
.cs-slide-enter-active,
.cs-slide-leave-active {
  transition: transform 0.28s ease;
}
.cs-slide-enter,
.cs-slide-leave-to {
  transform: translate(-50%, 100%);
}
</style>
